import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Settings as SettingsIcon } from 'lucide-react';
import { apiFetch } from '../api';
import { useAuth } from '../context/AuthContext';
import type { LayoutItem } from '../components/dashboard/types';
import { buildDefaultLayout, mergeLayout } from '../components/dashboard/registry';
import DashboardGrid from '../components/dashboard/DashboardGrid';
import DashboardConfigModal from '../components/dashboard/DashboardConfigModal';
import { PageHeader, Button } from '../components/ui';

function getGreeting(): string {
  const h = new Date().getHours();
  if (h < 5) return 'God nat';
  if (h < 10) return 'God morgen';
  if (h < 13) return 'God formiddag';
  if (h < 18) return 'God eftermiddag';
  if (h < 22) return 'God aften';
  return 'God nat';
}

function formatToday(): string {
  return new Date().toLocaleDateString('da-DK', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export default function DashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [layout, setLayout]       = useState<LayoutItem[]>([]);
  const [loading, setLoading]     = useState(true);
  const [configOpen, setConfigOpen] = useState(false);

  useEffect(() => {
    apiFetch('/api/dashboard/layout')
      .then(r => r.json())
      .then((saved: LayoutItem[]) => {
        setLayout(saved.length ? mergeLayout(saved) : buildDefaultLayout());
      })
      .catch(() => setLayout(buildDefaultLayout()))
      .finally(() => setLoading(false));
  }, []);

  async function saveLayout(items: LayoutItem[]) {
    const normalized = items.map((item, idx) => ({ ...item, position: idx }));
    setLayout(normalized);
    await apiFetch('/api/dashboard/layout', {
      method: 'PUT',
      body: JSON.stringify({ items: normalized }),
    });
  }

  const firstName = (user?.name || '').split(' ')[0];
  const greeting = firstName ? `${getGreeting()}, ${firstName}` : getGreeting();

  return (
    <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
      <PageHeader
        eyebrow={t('nav.dashboard')}
        title={greeting}
        subtitle={formatToday()}
        actions={
          <Button
            variant="ghost"
            size="sm"
            iconLeft={<SettingsIcon size={14} />}
            onClick={() => setConfigOpen(true)}
          >
            {t('dashboard.edit_widgets_btn')}
          </Button>
        }
      />

      {loading ? (
        <p className="text-sm text-ink-500">{t('dashboard.loading')}</p>
      ) : (
        <DashboardGrid items={layout} onReorder={saveLayout} />
      )}

      {configOpen && (
        <DashboardConfigModal
          items={layout}
          onSave={saveLayout}
          onClose={() => setConfigOpen(false)}
        />
      )}
    </main>
  );
}
